"use client"

import type { BlockchainFilter } from "@/app/blockchains/filters/blockchain-filters-scope"
import type { NftSearchItem } from "@anaconda/core/nft/search/types"
import type { PriceValue } from "@anaconda/core/price/types"
import { useLingui } from "@lingui/react/macro"
import {
  CommandGroup,
  CommandList,
  NavigationTabsList,
  NavigationTrigger,
  cn,
} from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { useCallback, useEffect, useRef, useState } from "react"
import { CollectionSearchBlockchainMoleculeReatom } from "./collection-search-blockchain-molecule.reatom"
import { HeaderNftSearchItem } from "./nft-search-item"
import { SearchBlockchainFilters } from "./search-blockchain-filters"

type SearchTab = "collections" | "items"

interface SearchPanelCollectionItem {
  id: string
  name: string
  image?: string
  floorPrice?: PriceValue
  verified?: boolean
}

interface SearchPanelProps {
  hasSearched: boolean
  collections?: SearchPanelCollectionItem[]
  nfts?: NftSearchItem[]
  isLoadingCollections?: boolean
  isLoadingNfts?: boolean
  isTrendingLoading?: boolean
  onCollectionClick?: (id: string) => void
  onNftClick?: (id: string) => void
  className?: string
  SearchCollectionItemComponent: React.ComponentType<{
    id: string
    name: string
    image: string | null | undefined
    floorPrice?: PriceValue
    verified?: boolean
    onSelect: () => void
    showBlockchain: boolean
  }>
  LoadingSkeletonComponent: React.ComponentType
}

function EmptyResults({ message }: { message: string }) {
  return (
    <div className="flex h-[320px] flex-col items-center justify-center gap-3">
      <div className="font-sans text-4xl text-foreground">¯\_(ツ)_/¯</div>
      <div className="font-medium text-foreground/60 text-sm">{message}</div>
    </div>
  )
}

function TabEmptyResults({ message }: { message: string }) {
  return (
    <div className="flex h-[320px] flex-col items-center justify-center px-3">
      <div className="font-medium text-foreground/60 text-sm">{message}</div>
    </div>
  )
}

function TrendingSection({
  title,
  isLoading,
  isEmpty,
  LoadingSkeletonComponent,
  children,
}: {
  title: string
  isLoading: boolean
  isEmpty: boolean
  LoadingSkeletonComponent: React.ComponentType
  children: React.ReactNode
}) {
  if (!isLoading && isEmpty) {
    return null
  }

  return (
    <div className="flex flex-col">
      <div className="px-1 py-2">
        <span className="font-medium text-foreground/60 text-sm">{title}</span>
      </div>
      {isLoading ? (
        <>
          {Array.from({ length: 3 }).map((_, i) => (
            <LoadingSkeletonComponent key={i} />
          ))}
        </>
      ) : (
        children
      )}
    </div>
  )
}

function SearchTabContent({
  isLoading,
  isEmpty,
  emptyMessage,
  LoadingSkeletonComponent,
  children,
}: {
  isLoading: boolean
  isEmpty: boolean
  emptyMessage: string
  LoadingSkeletonComponent: React.ComponentType
  children: React.ReactNode
}) {
  if (isLoading) {
    return (
      <>
        {Array.from({ length: 5 }).map((_, i) => (
          <LoadingSkeletonComponent key={i} />
        ))}
      </>
    )
  }

  if (isEmpty) {
    return <TabEmptyResults message={emptyMessage} />
  }

  return children
}

export const SearchPanelReatom = reatomComponent<SearchPanelProps>(
  ({
    hasSearched,
    collections = [],
    nfts = [],
    isLoadingCollections = false,
    isLoadingNfts = false,
    isTrendingLoading = false,
    onCollectionClick,
    onNftClick,
    className,
    SearchCollectionItemComponent,
    LoadingSkeletonComponent,
  }) => {
    const { t } = useLingui()
    const [activeTab, setActiveTab] = useState<SearchTab>("collections")
    const wasLoadingRef = useRef(false)

    const { selectedBlockchain, availableBlockchains, setSelectedBlockchain } =
      useMolecule(CollectionSearchBlockchainMoleculeReatom)

    const selectedBlockchainValue = selectedBlockchain()
    const availableBlockchainsValue = availableBlockchains()
    const showBlockchain = selectedBlockchainValue === "ALL"

    const handleBlockchainSelect = useCallback(
      (blockchain: BlockchainFilter) => {
        setSelectedBlockchain(blockchain)
      },
      [setSelectedBlockchain],
    )

    const defaultEmptyMessage = t`No results. NGMI.`
    const collectionsEmptyMessage = t`No collections found`
    const itemsEmptyMessage = t`No items found`

    const showTrending = !hasSearched
    const trendingLoading = showTrending && isTrendingLoading
    const trendingCollections = showTrending ? collections : []

    const searchCollections = hasSearched ? collections : []
    const searchNfts = hasSearched ? nfts : []

    const collectionsLoading = showTrending
      ? trendingLoading
      : isLoadingCollections
    const collectionsEmpty =
      !collectionsLoading &&
      (showTrending
        ? trendingCollections.length === 0
        : searchCollections.length === 0)

    const nftsLoading = hasSearched && isLoadingNfts
    const nftsEmpty = hasSearched && !nftsLoading && searchNfts.length === 0

    const bothSearchSectionsEmpty =
      hasSearched &&
      !isLoadingCollections &&
      !isLoadingNfts &&
      searchCollections.length === 0 &&
      searchNfts.length === 0

    const isSearchLoading = isLoadingCollections || isLoadingNfts

    useEffect(() => {
      if (!hasSearched) {
        setActiveTab("collections")
        wasLoadingRef.current = false
        return
      }

      if (isSearchLoading && !wasLoadingRef.current) {
        setActiveTab("collections")
      }

      wasLoadingRef.current = isSearchLoading
    }, [hasSearched, isSearchLoading])

    useEffect(() => {
      if (
        hasSearched &&
        !isLoadingCollections &&
        !isLoadingNfts &&
        searchCollections.length === 0 &&
        searchNfts.length > 0
      ) {
        setActiveTab("items")
      }
    }, [
      hasSearched,
      isLoadingCollections,
      isLoadingNfts,
      searchCollections.length,
      searchNfts.length,
    ])

    const displayCollections = showTrending
      ? trendingCollections
      : searchCollections

    const collectionsTabLabel =
      !isLoadingCollections && hasSearched
        ? t`Collections (${searchCollections.length})`
        : t`Collections`

    const itemsTabLabel =
      !isLoadingNfts && hasSearched ? t`Items (${searchNfts.length})` : t`Items`

    return (
      <div
        className={cn(
          "flex h-[480px] w-full flex-col rounded-md border border-foreground/20 bg-background shadow-lg backdrop-blur-sm",
          className,
        )}
        data-testid="search-panel"
      >
        <div className="flex-none overflow-visible px-1 py-2">
          <SearchBlockchainFilters
            blockchains={availableBlockchainsValue}
            selectedBlockchain={selectedBlockchainValue}
            onSelect={handleBlockchainSelect}
          />
        </div>

        {hasSearched && (
          <div className="flex-none px-5 pb-2">
            <NavigationTabsList size="sm" variant="tabs" className="gap-x-4">
              <NavigationTrigger active={activeTab === "collections"} asChild>
                <button
                  type="button"
                  onClick={() => setActiveTab("collections")}
                  data-testid="search-tab-collections"
                >
                  {collectionsTabLabel}
                </button>
              </NavigationTrigger>
              <NavigationTrigger active={activeTab === "items"} asChild>
                <button
                  type="button"
                  onClick={() => setActiveTab("items")}
                  data-testid="search-tab-items"
                >
                  {itemsTabLabel}
                </button>
              </NavigationTrigger>
            </NavigationTabsList>
          </div>
        )}

        <div className="flex flex-1 flex-col overflow-hidden px-5 pb-2">
          <div className="flex flex-1 flex-col overflow-y-auto">
            <CommandList className="border-none p-0 shadow-none">
              <CommandGroup className="p-0">
                {bothSearchSectionsEmpty ? (
                  <EmptyResults message={defaultEmptyMessage} />
                ) : showTrending ? (
                  <TrendingSection
                    title={t`Trending 24h`}
                    isLoading={collectionsLoading}
                    isEmpty={collectionsEmpty}
                    LoadingSkeletonComponent={LoadingSkeletonComponent}
                  >
                    {displayCollections.map((item) => (
                      <SearchCollectionItemComponent
                        key={item.id}
                        id={item.id}
                        name={item.name}
                        image={item.image ?? null}
                        floorPrice={item.floorPrice}
                        verified={item.verified}
                        onSelect={() => onCollectionClick?.(item.id)}
                        showBlockchain={showBlockchain}
                      />
                    ))}
                  </TrendingSection>
                ) : activeTab === "collections" ? (
                  <SearchTabContent
                    isLoading={collectionsLoading}
                    isEmpty={collectionsEmpty}
                    emptyMessage={collectionsEmptyMessage}
                    LoadingSkeletonComponent={LoadingSkeletonComponent}
                  >
                    {searchCollections.map((item) => (
                      <SearchCollectionItemComponent
                        key={item.id}
                        id={item.id}
                        name={item.name}
                        image={item.image ?? null}
                        floorPrice={item.floorPrice}
                        verified={item.verified}
                        onSelect={() => onCollectionClick?.(item.id)}
                        showBlockchain={showBlockchain}
                      />
                    ))}
                  </SearchTabContent>
                ) : (
                  <SearchTabContent
                    isLoading={nftsLoading}
                    isEmpty={nftsEmpty}
                    emptyMessage={itemsEmptyMessage}
                    LoadingSkeletonComponent={LoadingSkeletonComponent}
                  >
                    {searchNfts.map((nft) => (
                      <HeaderNftSearchItem
                        key={nft.id}
                        nft={nft}
                        onSelect={() => onNftClick?.(nft.id)}
                        showBlockchain={showBlockchain}
                      />
                    ))}
                  </SearchTabContent>
                )}
              </CommandGroup>
            </CommandList>
          </div>
        </div>
      </div>
    )
  },
  "SearchPanelReatom",
)
